<template>
    <side-panel v-if="form" open :kicker="title" :title="form.unit_address || ('Form #' + form.id)" :subtitle="tenantNames"
                :record-key="form.id" @close="$emit('close')">

        <div class="fp-status">
            <span v-for="s in statusList" :key="s.text" class="pill" :class="'pill-' + s.kind" :title="s.title || null">{{ s.text }}</span>
            <p v-if="meta('buildium_rent_error')" class="text-muted" style="margin:8px 0 0">
                <b>Why the rent is not in Buildium:</b> {{ meta('buildium_rent_error') }}. It is tried again every morning until the effective date;
                or enter it in Buildium yourself (Lease › Rent).
            </p>
            <p v-if="meta('send_refused')" class="text-muted" style="margin:8px 0 0;color:#B42318">
                <b>Not sent:</b> {{ (meta('send_refused') || {}).reason }}. Nothing reached the tenant.
            </p>
        </div>

        <div class="sp-facts" v-if="isRentIncrease(form)">
            <div><span>Current rent</span><b>{{ money(meta('rent_current')) || '—' }}</b></div>
            <div><span>New rent<template v-if="meta('increase_rate')"> (+{{ meta('increase_rate') }}%)</template></span><b>{{ money(meta('rent_new')) || '—' }}</b></div>
            <div><span>Takes effect</span><b>{{ dmy(meta('rent_new_payable_date')) || '—' }}</b></div>
            <div><span>Last increase</span><b>{{ dmy(meta('rent_last_increase_date') || meta('last_increase_date')) || '—' }}</b></div>
            <div v-if="form.form_type === 'rtb11a'"><span>Levies &amp; fees share ({{ meta('park_year') }})</span><b>{{ money((meta('calculation') || {}).box_e) || '—' }} / year</b></div>
        </div>
        <div class="sp-facts">
            <div><span>Assigned to</span><b>{{ form.user ? form.user.nickname : '—' }}</b></div>
            <div><span>Created</span><b>{{ date(form.created_at) }}</b></div>
        </div>

        <div class="sp-actions">
            <el-button v-if="canSend" type="primary" @click="$emit('confirm-send')">Confirm &amp; send to tenants</el-button>
            <el-button v-if="canConfirm" @click="$emit('confirm')">Confirm only</el-button>
            <el-button v-if="canForward" @click="$emit('forward')">Forward</el-button>
            <el-button @click="$emit('send')">Send by email</el-button>
            <el-dropdown trigger="click" @command="onMore">
                <el-button>More <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-if="form.form_type !== 'rtb11a'" command="open">Open the form</el-dropdown-item>
                    <el-dropdown-item command="download">Download PDF</el-dropdown-item>
                    <el-dropdown-item command="publish">Publish to Buildium</el-dropdown-item>
                    <el-dropdown-item command="delete" divided class="danger-item">Delete…</el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>
        </div>

        <section>
            <h3>Document</h3>
            <div class="fp-doc" v-if="preview">
                <iframe :src="pdfUrl" title="PDF preview"></iframe>
            </div>
            <div class="fp-doc-actions">
                <el-button size="small" v-if="!preview && pdfUrl" @click="preview = true">Show preview</el-button>
                <a v-if="pdfUrl" class="el-button el-button--small" :href="pdfUrl" target="_blank" rel="noopener">Open PDF</a>
                <a v-if="editUrl" class="el-button el-button--small" :href="editUrl" target="_blank" rel="noopener">Open the form</a>
            </div>
        </section>

        <section>
            <h3>Timeline</h3>
            <ol class="fp-timeline">
                <li v-for="(e, i) in timeline" :key="i" :class="{pending: e.pending}">
                    <b v-if="e.when">{{ e.when }}</b><span v-if="e.when"> · </span>{{ e.text }}
                    <div v-if="e.photos && e.photos.length" class="fp-photos">
                        <a v-for="p in e.photos" :key="p.id" :href="p.src" target="_blank" rel="noopener"><img :src="p.src" alt="Proof of service photo"></a>
                    </div>
                </li>
            </ol>
        </section>
    </side-panel>
</template>

<script>
import ApiService from "../utils/ApiService";
import SidePanel from "../components/SidePanel.vue";
import {FORM_TYPE_TITLES, formMeta, formStatus, formatMoney, formatDmy, formatDate, tenantsOf, tenantName, isRentIncrease} from "./formHelpers";

export default {
    name: "FormDetailPanel",
    components: {SidePanel},
    props: {form: {type: Object, default: null}, canSend: {type: Boolean, default: true}},
    data() {
        return {services: [], preview: false};
    },
    computed: {
        title() {
            return FORM_TYPE_TITLES[this.form.form_type] || this.form.form_type || 'Form';
        },
        tenantNames() {
            return tenantsOf(this.form).map(tenantName).filter(Boolean).join(', ');
        },
        statusList() {
            return formStatus(this.form);
        },
        auto() {
            return isRentIncrease(this.form) && !!this.meta('auto_generated');
        },
        canSend() {
            return this.canSend && this.form && isRentIncrease(this.form) && this.form.status === 'draft' && !formMeta(this.form, 'held_for');
        },
        canConfirm() {
            return this.auto && !this.meta('pm_confirmed_at') && this.form.status === 'draft';
        },
        canForward() {
            return isRentIncrease(this.form) && this.form.status === 'draft';
        },
        pdfUrl() {
            return this.form.links && this.form.links.pdf ? this.form.links.pdf.href : '';
        },
        editUrl() {
            return this.form.links && this.form.links.edit ? this.form.links.edit.href : '';
        },
        timeline() {
            const f = this.form;
            const who = f.user ? f.user.nickname : 'nobody';
            const via = this.meta('assigned_via');
            const events = [{
                when: this.date(f.created_at),
                text: this.auto
                    ? `Drafted automatically · assigned to ${who}` + (via === 'default_admin' ? ' (default administrator)' : via === 'rental_manager' ? ' (rental manager)' : '')
                    : `Created · ${who}`,
            }];
            if (this.meta('pm_confirmed_at')) {
                events.push({when: this.date(this.meta('pm_confirmed_at')), text: 'Confirmed by ' + (this.meta('pm_confirmed_by_name') || 'property manager')});
            } else if (this.auto) {
                events.push({pending: true, text: 'Waiting for confirmation by ' + who});
            }
            if (this.meta('review_requested_at')) {
                events.push({
                    when: this.date(this.meta('review_requested_at')),
                    text: `Sent to ${this.meta('review_by_name') || 'a property manager'} for signature` + (this.meta('review_requested_by_name') ? ` by ${this.meta('review_requested_by_name')}` : ''),
                });
            }
            if (f.status === 'review') {
                events.push({pending: true, text: `Waiting for ${this.meta('review_by_name') || 'the property manager'} to sign`});
            }
            if (f.sent_at) events.push({when: this.date(f.sent_at), text: 'Sent by email'});
            if (f.published_at) events.push({when: this.date(f.published_at), text: 'Published to Buildium'});
            this.services.forEach(s => {
                events.push({
                    when: this.date(s.served_at),
                    text: `${s.method_label}` + (s.user ? ` · recorded by ${s.user.nickname || s.user.email}` : '')
                        + (s.deemed_served_on ? ` · deemed served ${this.date(s.deemed_served_on)}` : '') + (s.note ? ` · ${s.note}` : ''),
                    photos: s.photos,
                });
            });
            if (isRentIncrease(f) && !this.services.length && !this.meta('served_at')) {
                events.push({pending: true, text: 'Proof of service — recorded from the Sterling Hub app'});
            }
            return events;
        },
    },
    watch: {
        form: {
            immediate: true,
            handler(now, before) {
                if (!now) return;
                if (!before || before.id !== now.id) {
                    this.preview = false;
                }
                this.loadServices();
            },
        },
    },
    methods: {
        meta(key) {
            return formMeta(this.form, key);
        },
        isRentIncrease,
        money: formatMoney,
        dmy: formatDmy,
        date: formatDate,
        loadServices() {
            const id = this.form.id;
            this.services = [];
            if (!isRentIncrease(this.form) && this.form.form_type !== 'rtb30') return;
            ApiService.get(`/sterling/forms/${id}/services`).then(r => {
                if (this.form && this.form.id === id) this.services = r.data || [];
            }).catch(() => {
                this.services = [];
            });
        },
        onMore(cmd) {
            if (cmd === 'open' && this.editUrl) window.open(this.editUrl, '_blank', 'noopener');
            if (cmd === 'download') this.$emit('download');
            if (cmd === 'publish') this.$emit('publish');
            if (cmd === 'delete') this.$emit('delete');
        },
    },
}
</script>

<style scoped>
.fp-doc-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.fp-doc iframe {
    width: 100%;
    height: 420px;
    border: 1px solid #E3E6EE;
    border-radius: 10px;
    margin-bottom: 10px;
}

.fp-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 13px;
}

.fp-timeline .pending {
    color: #5B6478;
}

.fp-photos {
    display: flex;
    gap: 6px;
    margin-top: 6px;
    flex-wrap: wrap;
}

.fp-photos img {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 6px;
}
</style>
